import React from 'react';
import { Box, Typography, Chip, Stack, IconButton, useTheme } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

export type CriticalCategory = 'self_harm' | 'medications' | 'allergies' | 'enemies_gang' | 'other';

export interface CriticalItem {
  id: string;
  category: CriticalCategory;
  snippet: string;
  detectedAgoSeconds: number;
  confidence: 'High' | 'Medium' | 'Low';
  status?: 'new' | 'confirmed' | 'unknown' | 'dismissed';
}

interface CriticalItemsPanelProps {
  items: CriticalItem[];
  onDelete: (id: string) => void;
  title?: string;
  compact?: boolean; // Hide header and wrapper styling when used inside tabs
}

const categoryMeta: Record<CriticalCategory, { label: string; color: string; bgColor: string }> = {
  self_harm: { label: 'Self-harm risk', color: '#dc2626', bgColor: '#fee2e2' },
  medications: { label: 'Medications', color: '#d97706', bgColor: '#fef3c7' },
  allergies: { label: 'Allergies', color: '#d97706', bgColor: '#fef3c7' },
  enemies_gang: { label: 'Enemies/Gang', color: '#7c3aed', bgColor: '#ede9fe' },
  other: { label: 'Key Item', color: '#2563eb', bgColor: '#dbeafe' }
};

export const CriticalItemsPanel: React.FC<CriticalItemsPanelProps> = ({
  items,
  onDelete,
  title = 'Critical Items',
  compact = false
}) => {
  const theme = useTheme();
  const activeItems = items;
  
  const formatTimeAgo = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s ago`;
    } else if (seconds < 600) { // Less than 10 minutes
      const minutes = Math.floor(seconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      // More than 10 minutes - show actual time
      const now = new Date();
      const itemTime = new Date(now.getTime() - seconds * 1000);
      return itemTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(compact ? {} : {
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          backgroundColor: 'background.paper',
          overflow: 'hidden'
        }),
        height: '100%'
      }}
    >
      {/* Header - Hidden in compact mode */}
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1,
            px: 2.5
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textAlign: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(107, 114, 128, 0.05)',
              transition: 'all 0.2s ease'
            }}
          >
            {title}
          </Typography>
          {activeItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 20,
                height: 20,
                borderRadius: '10px',
                backgroundColor: '#dc2626',
                color: 'white',
                fontSize: '11px',
                fontWeight: 600,
                px: 0.75
              }}
            >
              {activeItems.length}
            </Box>
          )}
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1, px: compact ? 0 : 2.5 }}>
        {items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              textAlign: 'left',
              fontSize: '14px',
              color: theme.palette.text.secondary
            }}
          >
            No items yet. Suggestions appear within a few seconds.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map((item) => {
              const meta = categoryMeta[item.category] || categoryMeta.other;
              
              return (
                <Box
                  key={item.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 1.5,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db',
                      boxShadow: theme.palette.mode === 'dark' 
                        ? '0 1px 3px rgba(255, 255, 255, 0.1)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  {/* Header Row */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: meta.color
                        }}
                      />
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: meta.color }}>
                        {meta.label}
                      </Typography>
                      <Chip
                        label={item.confidence}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '10px',
                          borderRadius: '8px',
                          backgroundColor: meta.bgColor,
                          color: meta.color,
                          fontWeight: 500,
                          '& .MuiChip-label': { px: 0.75, py: 0 }
                        }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                        {formatTimeAgo(item.detectedAgoSeconds)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(item.id)}
                        sx={{
                          p: 0.5,
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: '#dc2626',
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(220, 38, 38, 0.2)' 
                              : '#fee2e2'
                          }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {/* Snippet */}
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.text.primary,
                      lineHeight: 1.5
                    }}
                  >
                    {item.snippet}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default CriticalItemsPanel;


